export const stripTrailingSlash = (value: string): string => {
  if (!value) return value;
  return value.endsWith('/') ? value.slice(0, -1) : value;
};

export const ensureLeadingSlash = (value: string): string => {
  if (!value) return '/';
  return value.startsWith('/') ? value : `/${value}`;
};

export const joinUrl = (base: string, path: string): string => {
  if (!base) return path;
  const cleanBase = stripTrailingSlash(base);
  const cleanPath = ensureLeadingSlash(path);
  return `${cleanBase}${cleanPath}`;
};

export const toAbsoluteUrl = (maybeRelative: string, base: string | null | undefined): string => {
  if (!maybeRelative) return maybeRelative;
  if (maybeRelative.startsWith('http://') || maybeRelative.startsWith('https://')) return maybeRelative;
  if (!base) return ensureLeadingSlash(maybeRelative);
  return joinUrl(base, maybeRelative);
};

// Media is served from S3 with querystring auth, so every `.url` mints a FRESH
// presigned signature — the same image gets a different URL string on every
// render. On the auto-refreshing dashboard that made the browser re-download +
// re-decode every image each 10s cycle (visible blink). Cache the first signed
// URL we see for a given object PATH (everything before '?') and reuse it while
// it's fresh, so the src stays byte-identical across refreshes (cache hit, no
// blink). A genuinely new capture has a new path → new URL, so real updates
// still flow through. TTL is well inside the presign expiry so a long-lived tab
// re-mints before the signature would 403.
const SIGNED_URL_CACHE = new Map<string, { url: string; at: number }>();
const SIGNED_URL_TTL_MS = 10 * 60 * 1000;

// Ключ кеша — путь плюс параметры, кроме подписи. Живой кадр камеры
// (/equipment_discovery/live-frame/<cam>/?s=…&t=<время кадра>) живёт на ОДНОМ пути, а кадр
// меняет только t=: ключ по одному пути 10 минут отдавал первый кадр под подписью нового
// времени (Алексей 28.10 #3067/#3069, Tower: на фото 12:04, подписано 12:09).
const SIGNATURE_PARAM = /^(x-amz-|signature$|expires$|awsaccesskeyid$|s$)/i;
const stableKey = (url: string, q: number): string => {
  const kept = url.slice(q + 1).split('&').filter((kv) => kv && !SIGNATURE_PARAM.test(kv.split('=')[0]));
  return kept.length ? `${url.slice(0, q)}?${kept.join('&')}` : url.slice(0, q);
};

export const stableImageUrl = (url: string): string => {
  if (!url) return url;
  const q = url.indexOf('?');
  if (q < 0) return url; // unsigned / already stable
  const key = stableKey(url, q);
  const now = Date.now();
  const hit = SIGNED_URL_CACHE.get(key);
  if (hit && now - hit.at < SIGNED_URL_TTL_MS) return hit.url;
  SIGNED_URL_CACHE.set(key, { url, at: now });
  return url;
};
